import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { FreeFilterInputComponent } from '../free-filter-input/free-filter-input.component';
import { StateFilterCBComponent } from '../state-filter-combobox/state-cb.component';
import { Store } from '@ngxs/store';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { ChooseMedium } from './choose-medium.json.model';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Md5 } from 'ts-md5';
import { environment } from '../../../../environments/environment';
import { TranslocoModule } from '@jsverse/transloco';
import { AssetEntity } from '../../../asset/asset.entity';
import { SubjectEntity } from '../../../subject/subject.entity';
import { AssetEntityService } from '../../../asset/services/asset.entity.service';
import { BehaviorSubject, combineLatest, from, map, Observable, switchMap, tap } from 'rxjs';
import { SubjectEntityService } from '../../../subject/services/subject.entity.service';

import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { EntityStateModel } from '../../../core/state/entity.state';
import { makeQueryKey } from '../../entity-states/query-key';
import { AsyncPipe } from '@angular/common';
import { TableLoadingSpinnerComponent } from '../table-loading-spinner/table-loading-spinner';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BaseFilterComponent } from '../base-filter-component/base-filter-component';
import { IFilters } from '../base-filter-component/IFilter';

@Component({
  selector: 'pixl-choose-medium',
  imports: [
    MatDividerModule,
    MatDialogContent,
    MatDialogTitle,
    MatDialogActions,
    MatButtonModule,
    MatSortModule,
    MatTableModule,
    MatInputModule,
    MatSelectModule,
    MatIcon,
    MatTooltipModule,
    FreeFilterInputComponent,
    StateFilterCBComponent,
    TranslocoModule,
    MatPaginator,
    AsyncPipe,
    TableLoadingSpinnerComponent,
    FreeFilterInputComponent,
  ],
  templateUrl: './choose-medium.component.html',
  styleUrl: './choose-medium.component.scss'
})
export class ChooseMediumComponent extends BaseFilterComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;

  totalItems = 1000;

  private readonly store = inject(Store);
  private assetService = inject(AssetEntityService);
  private subjectService = inject(SubjectEntityService);
  assets$!: Observable<AssetEntity[]>;
  subjects$!: Observable<SubjectEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);
  private refresh$ = new BehaviorSubject<void>(undefined);
  private readonly pageChange$ = toObservable(this.pageChange);

  public filters: IFilters = {};
  private sortString = 'name';

  private readonly dialogRef = inject(MatDialogRef<ChooseMediumComponent>);
  private _liveAnnouncer = inject(LiveAnnouncer);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private displayedColumns: string[] = ['action', 'name', 'tags'];

  public dataSource = new MatTableDataSource<AssetEntity>([] as AssetEntity[]);
  public columnsToDisplay: string[] = this.displayedColumns.slice();
  public data = inject<ChooseMedium>(MAT_DIALOG_DATA);

  constructor() {
    super();
    if (this.data.mediaType === 'Image') {
      this.columnsToDisplay.push('image');
    }

    if (this.data.mediaType === 'Audio') {
      this.columnsToDisplay.push('audio');
    }
  }

  ngOnInit() {
    // set default statusFilter
    this.statusFilter = 'Live';
    this.loadRelationData();

    this.assets$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        const query: Record<string, string | number> = {
          'include': 'subjects',
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': this.sortString,
        };

        query['filter[mediaType]'] = this.data.mediaType !== undefined ? this.data.mediaType.toString() : '';
        query['filter[active]'] = 'true';

        if (this.freeFilter?.trim()?.length >= 3) {
          query['filter[nameLike]'] = this.freeFilter.trim();
        }

        if (this.subjectFilter?.length > 0) {
          query['filter[subjectId]'] = this.subjectFilter.join(',');
        }

        if (this.statusFilter?.trim()?.length >= 3) {
          query['filter[approval]'] = this.statusFilter.trim();
        }

        return from(this.assetService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.assetService.allByQuery(query))),
          map((entities: AssetEntity[]) => entities.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.assetService.stateClass) as EntityStateModel<AssetEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
            this.cdr.markForCheck();
          })
        );
      })
    );
  }

  ngAfterViewInit(): void {
    this.paginator().page.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event: PageEvent) => {
      this.pageChange.set({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  private loadRelationData(): void {
    const subjectQuery = { sort: 'name' };
    this.subjects$ = from(this.subjectService.ensureLoaded(subjectQuery)).pipe(
      switchMap(() => this.store.select(this.subjectService.allByQuery(subjectQuery)))
    );
  }

  private getBaseUrl(): string {
    if (environment.isProd) {
      return '/';
    }
    return 'https://zav-stage.westermann.de/';
  }

  private createMD5Path(fileName: string): string {
    let path = '';

    Md5
      .hashStr(fileName)
      .split(/(.{8})/)
      .filter(O => O)
      .forEach((entry, idx) => {
        path += ((idx > 0) ? '/' : '') + entry.toUpperCase();
      });

    return path;
  }

  public getImageUrl(asset: AssetEntity): string {
    let imageUrl = this.getBaseUrl();

    if (asset.mediaType === 'Image') {
      imageUrl += 'media/image/';
    }

    if (asset.mediaType === 'Animation') {
      imageUrl += 'media/animation/';
    }

    imageUrl += this.createMD5Path(asset.name);

    if (asset.mediaType === 'Image') {
      imageUrl += '.png';
    }

    if (asset.mediaType === 'Animation') {
      imageUrl += '.gif';
    }

    return imageUrl;
  }

  public getAudioUrl(asset: AssetEntity): string {
    let audioUrl = this.getBaseUrl() + 'media/audio/';

    audioUrl += this.createMD5Path(asset.name) + '.mp3';

    return audioUrl;
  }

  private sortData(sort: Sort): void {
    this.sortString = sort.direction === 'desc' ? `-${sort.active}` : sort.active;
    this.refresh$.next();
  }

  public announceSortChange(sortState: Sort) {
    if (sortState.direction) {
      this._liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
    }
    else {
      this._liveAnnouncer.announce('Sorting cleared');
    }
    this.sortData(sortState);
  }

  public getTags(asset: AssetEntity): string {
    let retValue = '';
    if (asset.tags && asset.tags.length > 0) {
      asset.tags.forEach((entry, idx) => {
        retValue += (idx > 0 ? ' ' : '') + `<button type="button" class="tags">${entry}</button>`;
      });
    }
    return retValue;
  }

  public getInitOption(): string {
    return 'Live';
  }

  public takeAsset(asset: AssetEntity): void {
    this.dialogRef.close(asset);
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
